import React from "react";
import * as Icon from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { RootState } from "../reduxToolkit/store";

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ComponentType<any>;
}

interface BreadcrumbContext {
  pathname: string;
  userRole: string;
  isAuthenticated: boolean;
}

interface BreadcrumbConfigEntry {
  match: string | RegExp; // exact path or regex pattern
  getTrail: (
    ctx: BreadcrumbContext,
    matchResult?: RegExpMatchArray
  ) => BreadcrumbItem[];
  roles?: string[]; // optional role gating
}

// Centralized breadcrumb configuration (ordered evaluation)
const breadcrumbConfig: BreadcrumbConfigEntry[] = [
  // Exact (string) matches
  {
    match: "/rmc/dashboard",
    getTrail: () => [{ label: "RMC Dashboard", icon: Icon.Speedometer2 }],
  },
  {
    match: "/admin/rmc",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      { label: "RMC Management", icon: Icon.Building },
    ],
  },
  {
    match: "/admin/dashboard",
    getTrail: () => [{ label: "Dashboard", icon: Icon.Speedometer2 }],
  },
  {
    match: "/admin/users",
    getTrail: ({ userRole }) => [
      {
        label: userRole === "RMCAdmin" ? "RMCAdmin" : "Admin",
        icon: Icon.ShieldCheck,
        path: "/admin/dashboard",
      },
      { label: "User Management", icon: Icon.People },
    ],
  },
  {
    match: "/admin/clientsmaster",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      { label: "Client Management", icon: Icon.PersonBadge },
    ],
  },
  {
    match: "/admin/vendors",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      { label: "Vendor Management", icon: Icon.Briefcase },
    ],
  },
  {
    match: "/admin/property",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      { label: "Property Management", icon: Icon.HouseDoor },
    ],
  },
  {
    match: "/properties",
    getTrail: () => [
      { label: "RMC", icon: Icon.PersonGear },
      { label: "Property Assignment", icon: Icon.HouseDoor },
    ],
  },
  {
    match: "/admin/facility",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      { label: "Facility Management", icon: Icon.Wrench },
    ],
  },
  {
    match: "/admin/amenities",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      { label: "Amenity Management", icon: Icon.Star },
    ],
  },
  {
    match: "/admin/complementary",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      { label: "Complementary Management", icon: Icon.Gift },
    ],
  },
  {
    match: "/admin/reservation",
    getTrail: ({ userRole }) => [
      {
        label: userRole === "Consultant" ? "Consultant" : "Admin",
        icon: userRole === "Consultant" ? Icon.PersonCheck : Icon.ShieldCheck,
        path: userRole === "Consultant" ? undefined : "/admin/dashboard",
      },
      { label: "Reservation Management", icon: Icon.CalendarCheck },
    ],
  },
  {
    match: "/admin/countries",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      { label: "Location Management", icon: Icon.GeoAlt },
    ],
  },
  {
    match: "/admin/room-type-categories",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      { label: "Room Categories", icon: Icon.Collection },
    ],
  },
  {
    match: "/admin/tier",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      { label: "Tier Management", icon: Icon.Trophy },
    ],
  },
  {
    match: "/clients", // legacy root clients list
    getTrail: ({ userRole }) => [
      {
        label: userRole.includes("RMC") ? "RMC" : "Admin",
        icon: userRole.includes("RMC") ? Icon.PersonGear : Icon.ShieldCheck,
      },
      {
        label: userRole === "RMCAdmin" ? "RMCAdmin" : "Admin",
        icon: userRole.includes("RMC") ? Icon.PersonGear : Icon.ShieldCheck,
      },
      { label: "Client Management", icon: Icon.Building },
    ],
  },
  {
    match: "/my-profile",
    getTrail: () => [
      { label: "Account", icon: Icon.Person },
      { label: "My Profile", icon: Icon.PersonGear },
    ],
  },
  {
    match: "/manage-property/add",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      {
        label: "Property Management",
        icon: Icon.HouseDoor,
        path: "/admin/property",
      },
      { label: "Add Property", icon: Icon.PlusSquare },
    ],
  },
  {
    match: "/manage-property/edit/:propertyId",
    getTrail: () => [
      { label: "Dashboard", icon: Icon.ShieldCheck, path: "/admin/dashboard" },
      {
        label: "Property Management",
        icon: Icon.HouseDoor,
        path: "/admin/property",
      },
      { label: "Edit Property", icon: Icon.PencilSquare },
    ],
  },
  // Dynamic patterns (RegExp) - include path references for intermediate breadcrumb navigation
  {
    match: /^\/rmc\/([^/]+)\/clients$/, // Legacy /rmc/:rmcId/clients
    getTrail: (_ctx, m) => {
      const rmcId = m?.[1];
      return [
        { label: "RMC", icon: Icon.Building, path: "/admin/rmc" },
        { label: "Client Management", icon: Icon.Building },
      ];
    },
  },
  {
    match: /^\/admin\/rmc\/([^/]+)\/clients$/, // /admin/rmc/:rmcId/clients
    getTrail: (_ctx, m) => {
      return [
        {
          label: "Dashboard",
          icon: Icon.ShieldCheck,
          path: "/admin/dashboard",
        },
        { label: "RMC Management", icon: Icon.Building, path: "/admin/rmc" },
        { label: "Client Management", icon: Icon.Building },
      ];
    },
  },
  {
    match: /^\/rmc\/([^/]+)\/clients\/([^/]+)\/proposal$/, // New proposal route
    getTrail: (_ctx, m) => {
      const rmcId = m?.[1];
      return [
        { label: "RMC", icon: Icon.Building, path: "/admin/rmc" },
        {
          label: "Client Management",
          icon: Icon.Building,
          path: rmcId ? `/rmc/${rmcId}/clients` : undefined,
        },
        { label: "Manage Proposal", icon: Icon.FileEarmarkPlus },
      ];
    },
  },
  {
    match: /^\/rmc\/([^/]+)\/clients\/([^/]+)\/properties$/, // Legacy kept temporarily
    getTrail: (_ctx, m) => {
      const rmcId = m?.[1];
      return [
        { label: "RMC", icon: Icon.Building, path: "/admin/rmc" },
        {
          label: "Client Management",
          icon: Icon.Building,
          path: rmcId ? `/rmc/${rmcId}/clients` : undefined,
        },
        { label: "Create Proposal", icon: Icon.FileEarmarkPlus },
      ];
    },
  },
  {
    match: /^\/admin\/rmc\/([^/]+)\/clients\/([^/]+)\/properties$/,
    getTrail: (_ctx, m) => {
      const rmcId = m?.[1];
      return [
        {
          label: "Dashboard",
          icon: Icon.ShieldCheck,
          path: "/admin/dashboard",
        },
        { label: "RMC Management", icon: Icon.Building, path: "/admin/rmc" },
        {
          label: "Client Management",
          icon: Icon.Building,
          path: rmcId ? `/admin/rmc/${rmcId}/clients` : undefined,
        },
        { label: "Property Assignment", icon: Icon.HouseDoor },
      ];
    },
  },
  {
    match: /^\/rmc\/([^/]+)\/clients\/([^/]+)\/properties\/([^/]+)\/rooms$/, // Legacy /rmc/:rmcId/clients/:clientId/properties/:propertyId/rooms
    getTrail: (_ctx, m) => {
      const rmcId = m?.[1];
      return [
        { label: "RMC", icon: Icon.Building, path: "/admin/rmc" },
        {
          label: "Client Management",
          icon: Icon.Building,
          path: rmcId ? `/rmc/${rmcId}/clients` : undefined,
        },
        { label: "Property Assignment", icon: Icon.HouseDoor },
        { label: "Rooms", icon: Icon.DoorClosed },
      ];
    },
  },
  {
    match:
      /^\/admin\/rmc\/([^/]+)\/clients\/([^/]+)\/properties\/([^/]+)\/rooms$/,
    getTrail: (_ctx, m) => {
      const rmcId = m?.[1];
      return [
        {
          label: "Dashboard",
          icon: Icon.ShieldCheck,
          path: "/admin/dashboard",
        },
        { label: "RMC Management", icon: Icon.Building, path: "/admin/rmc" },
        {
          label: "Client Management",
          icon: Icon.Building,
          path: rmcId ? `/admin/rmc/${rmcId}/clients` : undefined,
        },
        { label: "Property Assignment", icon: Icon.HouseDoor },
        { label: "Rooms", icon: Icon.DoorClosed },
      ];
    },
  },
];

const Breadcrumb: React.FC = () => {
  const location = useLocation();
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const userRole = useSelector((state: RootState) => state.auth.userRole) ?? "";

  // Gate access (same logic as before)
  const hasAdminAccess = [
    "Administrator",
    "Manager",
    "RMCAdmin",
    "RMCUser",
    "Consultant",
  ].includes(userRole);
  if (!isAuthenticated || !hasAdminAccess) return null;

  const ctx: BreadcrumbContext = {
    pathname: location.pathname,
    userRole,
    isAuthenticated,
  };

  let resolved: BreadcrumbItem[] = [];
  for (const entry of breadcrumbConfig) {
    if (typeof entry.match === "string") {
      if (entry.match === location.pathname) {
        if (!entry.roles || entry.roles.includes(userRole)) {
          resolved = entry.getTrail(ctx);
          break;
        }
      }
    } else {
      const m = location.pathname.match(entry.match);
      if (m) {
        if (!entry.roles || entry.roles.includes(userRole)) {
          resolved = entry.getTrail(ctx, m);
          break;
        }
      }
    }
  }

  if (!resolved.length) return null; // nothing matched

  return (
    <>
      <nav aria-label="breadcrumb" className="mb-0 breadcrumbbar">
        <div className="container-fluid pt-2 pb-2">
          <div className="container d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3 flex-nowrap">
              <ol className="breadcrumb rounded mb-0">
                {resolved.map((crumb, index) => {
                  const isLast = index === resolved.length - 1;
                  const CrumbIcon = crumb.icon;
                  return (
                    <li
                      key={index}
                      className={`breadcrumb-item ${isLast ? "active" : ""}`}
                      {...(isLast && { "aria-current": "page" })}
                    >
                      {isLast ? (
                        <span className="d-flex align-items-center text-muted">
                          {CrumbIcon && (
                            <CrumbIcon className="me-1" size={14} />
                          )}
                          {crumb.label}
                        </span>
                      ) : crumb.path ? (
                        <Link
                          to={crumb.path}
                          className="text-decoration-none text-primary d-flex align-items-center"
                        >
                          {CrumbIcon && (
                            <CrumbIcon className="me-1" size={14} />
                          )}
                          {crumb.label}
                        </Link>
                      ) : (
                        <span className="d-flex align-items-center text-secondary">
                          {CrumbIcon && (
                            <CrumbIcon className="me-1" size={14} />
                          )}
                          {crumb.label}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div
                id="before-page-title"
                className="d-flex align-items-center"
              ></div>
              <div
                id="page-title"
                className="page-title fw-semibold text-dark"
              ></div>
              <div
                id="page-header-slot"
                className="d-flex align-items-center gap-2 flex-wrap"
              ></div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Breadcrumb;
